'use client'

import * as React from 'react'
import * as AccordionPrimitive from 'primitives/accordion'
import { ChevronUpIcon } from 'primitives/icons'
import { tw } from 'utils/classnames'

const accordionDownIcon = (
  <ChevronUpIcon
    className="size-5 text-[#2a1f3f] transition-transform group-data-[state='open']/trigger:rotate-180"
    aria-hidden
  />
)

export function SingleAccordion({
  isInitialExpanded,
  header,
  headerClasses,
  children,
}: {
  isInitialExpanded?: boolean
  header: string
  headerClasses?: string
  children: React.ReactNode
}) {
  const [value, setValue] = React.useState(isInitialExpanded ? header : undefined)

  const contentRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setValue(undefined)
    }

    const updateHeight = () => {
      if (!contentRef.current) {
        return
      }

      const element = contentRef.current

      // Reset height calculation
      contentRef.current.style.setProperty('--radix-collapsible-content-height', 'auto')

      // Force recalculation on next frame
      requestAnimationFrame(() => {
        const height = element.scrollHeight
        element.style.setProperty('--radix-collapsible-content-height', `${height}px`)
      })
    }

    if (!contentRef.current) {
      return
    }

    const observer = new MutationObserver(updateHeight)

    observer.observe(contentRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [])

  return (
    <AccordionPrimitive.Root type="single" collapsible value={value} onValueChange={setValue}>
      <AccordionPrimitive.Item value={header}>
        <AccordionPrimitive.Header>
          <AccordionPrimitive.Trigger
            className="flex h-5 items-center justify-between hover:cursor-pointer"
            icon={accordionDownIcon}
          >
            <span className={tw('text-14 font-bold', headerClasses)}>{header}</span>
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
        <AccordionPrimitive.Content ref={contentRef}>{children}</AccordionPrimitive.Content>
      </AccordionPrimitive.Item>
    </AccordionPrimitive.Root>
  )
}
